import {
	INSPIRATION_DEFAULT_SCENARIO,
	INSPIRATION_SCENARIO_KEYS,
	INSPIRATION_SCENARIO_RATES,
} from "web_myplann/Inspiration/constants";
import {
	EARLY_FREEDOM_LIFESTYLE_ANCHORS,
	EARLY_FREEDOM_SAFE_WITHDRAWAL_YEARS,
	EARLY_FREEDOM_SAVING_FALLBACK,
	EARLY_FREEDOM_TARGET_FALLBACK,
} from "web_myplann/Inspiration/EarlyFreedom/constants";
import type {
	EarlyFreedomAnchorKey,
	EarlyFreedomBaseline,
	EarlyFreedomContext,
	EarlyFreedomInputs,
	EarlyFreedomScenario,
	EarlyFreedomScenarioKey,
} from "web_myplann/Inspiration/EarlyFreedom/Types/EarlyFreedomTypes";
import { getEarlyFreedomLevel } from "web_myplann/Inspiration/EarlyFreedom/Utils/getEarlyFreedomLevel";
import { getEarlyFreedomYears } from "web_myplann/Inspiration/EarlyFreedom/Utils/getEarlyFreedomYears";
import { MONTHS_IN_YEAR } from "web_myplann/Shared/constants";

const EARLY_FREEDOM_READY_COVERAGE = 90;

export const getEarlyFreedomScenario = (
	context: EarlyFreedomContext,
	key: EarlyFreedomScenarioKey,
): EarlyFreedomScenario => {
	const scenario = context.scenarios.find((candidate) => candidate.key === key);
	if (!scenario) throw new Error(`Missing early freedom scenario: ${key}`);

	return scenario;
};

export const getEarlyFreedomScenarioRatePercent = (
	scenario: EarlyFreedomScenario,
): number => Math.round(scenario.rate * 100);

const ANCHOR_KEY_BY_AMOUNT = new Map<number, EarlyFreedomAnchorKey>(
	EARLY_FREEDOM_LIFESTYLE_ANCHORS.map(
		(anchor) => [anchor.amount, anchor.key] as const,
	),
);

export const computeEarlyFreedomContext = (
	inputs: EarlyFreedomInputs,
	baseline: EarlyFreedomBaseline = {},
): EarlyFreedomContext => {
	const targetExpenses = inputs.targetExpenses ?? EARLY_FREEDOM_TARGET_FALLBACK;
	const monthlySaving =
		inputs.monthlySaving ??
		baseline.monthlySaving ??
		EARLY_FREEDOM_SAVING_FALLBACK;
	const rentNet = inputs.rentNet ?? 0;
	const passiveToday = rentNet;

	const gap = Math.max(0, targetExpenses - passiveToday);
	const coverageRatio =
		targetExpenses > 0
			? Math.min(100, Math.round((passiveToday / targetExpenses) * 100))
			: 0;
	const targetCapital =
		gap * MONTHS_IN_YEAR * EARLY_FREEDOM_SAFE_WITHDRAWAL_YEARS;
	const level = getEarlyFreedomLevel(inputs.targetExpenses, passiveToday);

	const scenarioFor = (
		key: EarlyFreedomScenarioKey,
		rate: number,
	): EarlyFreedomScenario => {
		const years = getEarlyFreedomYears(monthlySaving, rate, targetCapital);
		return {
			key,
			rate,
			years,
			targetAge:
				baseline.age !== undefined && years !== null
					? baseline.age + years
					: null,
		};
	};

	const activeAnchorKey = ANCHOR_KEY_BY_AMOUNT.get(targetExpenses) ?? "luxury";

	return {
		level,
		age: baseline.age,
		targetExpenses,
		monthlySaving,
		rentNet,
		passiveToday,
		gap,
		coverageRatio,
		isEarlyFreedomReady:
			level !== "cold" && coverageRatio >= EARLY_FREEDOM_READY_COVERAGE,
		targetCapital,
		scenarios: INSPIRATION_SCENARIO_KEYS.map((key) =>
			scenarioFor(key, INSPIRATION_SCENARIO_RATES[key]),
		),
		selectedScenario: inputs.scenario ?? INSPIRATION_DEFAULT_SCENARIO,
		activeAnchorKey,
	};
};
